import React, { useEffect, useState } from "react";
import "./banking.css";
import { useDispatch, useSelector } from "react-redux";

/* ---------------- Utility Functions ---------------- */

// Mask bank code → show only last 2 digits
const maskCode = (code) => (code ? `XXX${code.slice(-2)}` : "-");

/* ---------------- Component ---------------- */

const BankingPartner = () => {
  const { bankList, loading } = useSelector((state) => state.bankList || {});

  const [banks, setBanks] = useState([]);
  const [filteredBanks, setFilteredBanks] = useState([]);
  const [bankRows, setBankRows] = useState({ row1: [], row2: [], row3: [] });

  const [loanTypes, setLoanTypes] = useState([
    { id: "all", label: "All Loans" },
  ]);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLoanType, setSelectedLoanType] = useState("all");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  /* ---------------- Fetch Banks ---------------- */

  useEffect(() => {
    if (!bankList?.length) return;

    try {
      const activeBanks = bankList.filter(
        (b) => b.is_active === 1 || b.is_active === "1" || b.is_active === true,
      );

      /* Extract Loan Types */
      const uniqueLoanTypes = new Set();
      activeBanks.forEach((bank) => {
        if (Array.isArray(bank.sub_service_slug)) {
          bank.sub_service_slug.forEach((type) => uniqueLoanTypes.add(type));
        }
      });

      const loanTypeList = [
        { id: "all", label: "All Loans" },
        ...Array.from(uniqueLoanTypes).map((type) => ({
          id: type,
          label: type
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase()),
        })),
      ];
      setLoanTypes(loanTypeList);

      // Shuffle logos for scrolling rows
      const shuffled = [...activeBanks].sort(() => Math.random() - 0.5);
      const perRow = Math.ceil(shuffled.length / 3);

      setBankRows({
        row1: shuffled.slice(0, perRow),
        row2: shuffled.slice(perRow, perRow * 2),
        row3: shuffled.slice(perRow * 2),
      });

      setBanks(activeBanks);
      setFilteredBanks(activeBanks);
    } catch (error) {
      console.error("Bank fetch error:", error);
    }
  }, [bankList]);

  /* ---------------- Filter Logic ---------------- */

  useEffect(() => {
    let data = [...banks];

    // Dropdown Loan Type filter
    if (selectedLoanType !== "all") {
      data = data.filter(
        (b) =>
          Array.isArray(b.sub_service_slug) &&
          b.sub_service_slug.includes(selectedLoanType),
      );
    }

    // Search filter (Bank Name + Short Name + Loan Type)
    if (searchTerm.trim() !== "") {
      const term = searchTerm.toLowerCase();

      data = data.filter((b) => {
        const bankName = b.bank_name?.toLowerCase() || "";
        const shortName = b.bank_short_name?.toLowerCase() || "";

        const loanTypes = Array.isArray(b.sub_service_slug)
          ? b.sub_service_slug.join(" ").toLowerCase()
          : "";

        return (
          bankName.includes(term) ||
          shortName.includes(term) ||
          loanTypes.includes(term)
        );
      });
    }

    setFilteredBanks(data);
    setCurrentPage(1);
  }, [searchTerm, selectedLoanType, banks]);

  /* ---------------- Pagination ---------------- */

  const totalPages = Math.ceil(filteredBanks.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedBanks = filteredBanks.slice(
    startIndex,
    startIndex + rowsPerPage,
  );
  if (loading) {
    return (
      <div className="page-loader">
        {/* <div className="loading-spinner"></div> */}
        <p>Loading Banking Partners...</p>
      </div>
    );
  }

  /* ---------------- JSX ---------------- */

  return (
    <div className="banking">
      {/* ================= Logos Section ================= */}
      <section className="banks-partner-section">
        <div className="section-header">
          <h2>Our 100+ Banking Partners</h2>
          <p>Trusted by India's Leading Financial Institutions</p>
        </div>

        <div className="banks-listing-container">
          {Object.values(bankRows).map(
            (row, idx) =>
              row.length > 0 && (
                <ul
                  key={idx}
                  className={`banks-row ${
                    idx % 2 === 0 ? "scroll-left" : "scroll-right"
                  }`}
                >
                  {[...row, ...row].map((bank, i) => (
                    <li key={`${idx}-${i}`}>
                      <img
                        src={bank.bank_logo_url}
                        alt={bank.bank_name}
                        className="bank-logo"
                        loading="lazy"
                      />
                    </li>
                  ))}
                </ul>
              ),
          )}
        </div>
      </section>

      {/* ================= Search & Filter ================= */}
      <div className="hp-sticky-wrapper">
        <div className="hp-search-bar">
          <h6>Our Banking Partner</h6>

          <input
            type="text"
            className="hp-search-input"
            placeholder="Search bank"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <select
            className="hp-dropdown"
            value={selectedLoanType}
            onChange={(e) => setSelectedLoanType(e.target.value)}
          >
            {loanTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ================= Table ================= */}
      <div className="table-wrapper">
        <div className="banking-container">
          {paginatedBanks.length === 0 ? (
            <div className="no-banks">No banks found</div>
          ) : (
            <table className="banking-table">
              <thead>
                <tr>
                  <th>Bank</th>
                  <th>Short Name</th>
                  <th>Full Name</th>
                  <th>Loan Types</th>
                  <th>Code</th>
                </tr>
              </thead>
              <tbody>
                {paginatedBanks.map((bank) => (
                  <tr key={bank.id}>
                    <td>
                      <img
                        src={bank.bank_logo_url}
                        alt={bank.bank_name}
                        className="table-logo"
                      />
                    </td>
                    <td>{bank.bank_short_name}</td>
                    <td>{bank.bank_name}</td>
                    <td>
                      {Array.isArray(bank.sub_service_slug)
                        ? bank.sub_service_slug
                            .map((type) => {
                              const text = type
                                .replace(/_/g, " ")
                                .toLowerCase();
                              return (
                                text.charAt(0).toUpperCase() + text.slice(1)
                              );
                            })
                            .join(", ")
                        : "-"}
                    </td>
                    <td>{maskCode(bank.bank_code)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="help-pagination">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                Prev
              </button>

              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  className={`button-counter ${currentPage === i + 1 ? "active" : ""}`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}

              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BankingPartner;
